import { ReportingBucket } from '../types/buckets';
import type {
  IKeyedSeries,
  ISeriesPoint,
  IWaterfallStep,
} from '../types/charts';
import type { IFactTable, IMonthFacts, IPeriodFacts } from '../types/metrics';
import type { MonthKey } from '../types/period';
import { BUCKET_LABELS, OPEX_BUCKETS } from './BucketHelper';
import { MetricsHelper } from './MetricsHelper';
import { MonthKeyHelper } from './MonthKeyHelper';

interface IBridgeInput {
  key: string;
  label: string;
  value: number;
}

interface ITrendRow {
  month: MonthKey;
  label: string;
  row: IMonthFacts | undefined;
  preliminary: boolean;
}

/** Months a trend chart plots; `includePreliminary` opts into open months, which no aggregate ever sees. */
export interface ITrendMonths {
  months: MonthKey[];
  includePreliminary?: boolean;
}

/** Shapes fact-table data into the row format the chart primitives consume. */
export class ChartModelHelper {
  /** Runway is capped so a near-zero burn does not flatten the rest of the chart. */
  static readonly RUNWAY_CAP_MONTHS = 60;

  /** The 3-month average only ever uses closed months, so it stops at the boundary. */
  static revenueTrend(table: IFactTable, trend: ITrendMonths): ISeriesPoint[] {
    const rows = ChartModelHelper.trendRows(table, trend);
    const average = MetricsHelper.movingAverage(
      rows.map((r) => (r.preliminary ? null : (r.row?.pnl.revenue ?? null))),
      3
    );
    return rows.map(({ month, label, row, preliminary }, i) => ({
      month,
      label,
      preliminary,
      revenue: row?.pnl.revenue ?? null,
      movingAverage: preliminary ? null : (average[i] ?? null),
    }));
  }

  /** Expenses are everything between revenue and net profit so the bars always reconcile to the line. */
  static profitTrend(table: IFactTable, trend: ITrendMonths): ISeriesPoint[] {
    return ChartModelHelper.trendRows(table, trend).map(
      ({ month, label, row, preliminary }) => ({
        month,
        label,
        preliminary,
        revenue: row?.pnl.revenue ?? null,
        expenses: row ? row.pnl.revenue - row.pnl.netProfit : null,
        netProfit: row?.pnl.netProfit ?? null,
      })
    );
  }

  static revenueMix(table: IFactTable, months: MonthKey[]): IKeyedSeries {
    const revenueLines = table.lines.filter(
      (l) =>
        l.section === 'revenue' &&
        table.bucketMap.get(l.id) === ReportingBucket.REVENUE
    );
    const data = months.map((month) => {
      const row = table.byMonth.get(month);
      const values = Object.fromEntries(
        revenueLines.map((l) => [l.id, row ? (row.lines[l.id] ?? 0) : null])
      );
      return { month, label: MonthKeyHelper.shortLabel(month), ...values };
    });
    const shown = revenueLines.filter((l) =>
      ChartModelHelper.hasValues(data, l.id)
    );
    const taken = new Set<string>();
    const keys = shown.map((l) => {
      let label = l.name;
      for (let n = 2; taken.has(label); n += 1) label = `${l.name} (${n})`;
      taken.add(label);
      return { key: l.id, label };
    });
    return { data, keys };
  }

  static marginTrend(table: IFactTable, trend: ITrendMonths): ISeriesPoint[] {
    return ChartModelHelper.trendRows(table, trend).map(
      ({ month, label, row, preliminary }) => ({
        month,
        label,
        preliminary,
        grossMargin: row?.pnl.grossMargin ?? null,
        ebitdaMargin: row?.pnl.ebitdaMargin ?? null,
        netMargin: row?.pnl.netMargin ?? null,
      })
    );
  }

  static opexBreakdown(table: IFactTable, trend: ITrendMonths): IKeyedSeries {
    const data = ChartModelHelper.trendRows(table, trend).map(
      ({ month, label, row, preliminary }) => ({
        month,
        label,
        preliminary,
        ...Object.fromEntries(
          OPEX_BUCKETS.map((b) => [b, row ? row.buckets[b] : null])
        ),
      })
    );
    const keys = OPEX_BUCKETS.filter((b) =>
      ChartModelHelper.hasValues(data, b)
    ).map((b) => ({ key: b, label: BUCKET_LABELS[b] }));
    return { data, keys };
  }

  static payrollTrend(table: IFactTable, trend: ITrendMonths): ISeriesPoint[] {
    return ChartModelHelper.trendRows(table, trend).map(
      ({ month, label, row, preliminary }) => ({
        month,
        label,
        preliminary,
        payroll: row?.pnl.payroll ?? null,
        nonPayroll: row?.pnl.nonPayrollOpex ?? null,
        payrollShare:
          row && row.pnl.opex ? row.pnl.payroll / row.pnl.opex : null,
      })
    );
  }

  /** Opening cash → indirect-method movements → closing cash, as floating waterfall bars. */
  static cashBridge(facts: IPeriodFacts): IWaterfallStep[] {
    const cf = facts.cashFlow;
    if (!cf.hasOpening) return [];
    const movements: IBridgeInput[] = [
      { key: 'netIncome', label: 'Net income', value: cf.netIncome },
      { key: 'da', label: 'D&A', value: cf.depreciationAmortisation },
      { key: 'wc', label: 'Working capital', value: cf.workingCapitalChange },
      { key: 'capex', label: 'Capex', value: -cf.capex },
      {
        key: 'investments',
        label: 'Investments',
        value: -cf.investmentsPurchased - cf.otherInvesting,
      },
      { key: 'debt', label: 'Debt', value: cf.debtRaised },
      {
        key: 'equity',
        label: 'Equity',
        value: cf.equityRaised + cf.otherFinancing,
      },
      { key: 'other', label: 'Other', value: cf.unexplained },
    ].filter(
      (m) => m.value !== 0 && (m.key !== 'other' || Math.abs(m.value) >= 1)
    );

    const deltas = movements.reduce<{
      running: number;
      steps: IWaterfallStep[];
    }>(
      (acc, m) => {
        const next = acc.running + m.value;
        return {
          running: next,
          steps: [
            ...acc.steps,
            {
              ...m,
              kind: 'delta',
              base: Math.min(acc.running, next),
              height: Math.abs(m.value),
            },
          ],
        };
      },
      { running: cf.openingCash, steps: [] }
    );

    return [
      {
        key: 'open',
        label: 'Opening cash',
        value: cf.openingCash,
        kind: 'start',
        base: 0,
        height: cf.openingCash,
      },
      ...deltas.steps,
      {
        key: 'close',
        label: 'Closing cash',
        value: cf.closingCash,
        kind: 'end',
        base: 0,
        height: cf.closingCash,
      },
    ];
  }

  /** Runway needs a trailing-3 burn, which only exists for closed months, so open months plot burn and cash only. */
  static burnRunway(table: IFactTable, trend: ITrendMonths): ISeriesPoint[] {
    return ChartModelHelper.trendRows(table, trend).map(
      ({ month, label, row, preliminary }) => {
        const cash = row?.balance.cashAndInvestments ?? null;
        if (!row || !row.cashFlow.hasOpening) {
          return {
            month,
            label,
            preliminary,
            netBurn: null,
            runway: null,
            cash,
          };
        }
        const runway = preliminary
          ? null
          : MetricsHelper.runwayFromBurn(
              row.balance.cashAndInvestments,
              MetricsHelper.trailing3Burn(table, month)
            );
        return {
          month,
          label,
          preliminary,
          netBurn: row.cashFlow.netBurn,
          runway:
            runway === null
              ? null
              : Math.min(runway, ChartModelHelper.RUNWAY_CAP_MONTHS),
          cash,
        };
      }
    );
  }

  /** Re-keys series values to human labels so legends and tooltips read naturally. */
  static relabel(
    rows: ISeriesPoint[],
    labels: Record<string, string>
  ): Record<string, string | number | boolean | null>[] {
    return rows.map((row) => ({
      label: row.label,
      preliminary: row.preliminary ?? false,
      ...Object.fromEntries(
        Object.entries(labels).map(([from, to]) => [to, row[from] ?? null])
      ),
    }));
  }

  static hasSeries(rows: ISeriesPoint[], key: string): boolean {
    return rows.some((row) => row[key] !== null && row[key] !== undefined);
  }

  private static hasValues(rows: ISeriesPoint[], key: string): boolean {
    return rows.some((row) => row[key] !== null && row[key] !== 0);
  }

  /**
   * Closed months come from `byMonth`; open months are read from
   * `preliminaryByMonth` only when asked for, and are flagged so the chart
   * primitives can draw them differently. Nothing here feeds an aggregate.
   */
  private static trendRows(
    table: IFactTable,
    { months, includePreliminary = false }: ITrendMonths
  ): ITrendRow[] {
    return months.map((month) => {
      const closed = table.byMonth.get(month);
      const row =
        closed ??
        (includePreliminary ? table.preliminaryByMonth.get(month) : undefined);
      return {
        month,
        label: MonthKeyHelper.shortLabel(month),
        row,
        preliminary: !closed && row !== undefined,
      };
    });
  }
}
